import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
// import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { apiRequest } from "../lib/queryClient";
import { queryClient } from "../lib/queryClient";
import { useToast } from "../hooks/use-toast";

type SettingsData = {
  general: {
    siteName: string;
    refreshInterval: number;
    defaultDashboardView: string;
  };
  notifications: {
    emailEnabled: boolean;
    emailRecipients: string;
    smsEnabled: boolean;
    smsRecipients: string;
    slackEnabled: boolean;
    slackWebhook: string;
  };
  thresholds: {
    cpuWarning: number;
    cpuCritical: number;
    memoryWarning: number;
    memoryCritical: number;
    diskWarning: number;
    diskCritical: number;
  };
  snmp: {
    communityString: string;
    version: string;
    port: number;
    timeout: number;
    retries: number;
  };
  ai: {
    enabled: boolean;
    openaiApiKey: string;
    model: string;
  };
};

export default function Settings() {
  const { toast } = useToast();
  
  const { data: settings, isLoading } = useQuery<SettingsData>({
    queryKey: ["/api/settings"],
  });

  // Default settings if API doesn't return data
  const defaultSettings: SettingsData = {
    general: {
      siteName: "NetMonitor Pro",
      refreshInterval: 30,
      defaultDashboardView: "summary",
    },
    notifications: {
      emailEnabled: true,
      emailRecipients: "admin@example.com",
      smsEnabled: false,
      smsRecipients: "",
      slackEnabled: true,
      slackWebhook: "https://hooks.slack.com/services/XXXXX/XXXXX/XXXXX",
    },
    thresholds: {
      cpuWarning: 70,
      cpuCritical: 90,
      memoryWarning: 80,
      memoryCritical: 95,
      diskWarning: 80,
      diskCritical: 95,
    },
    snmp: {
      communityString: "public",
      version: "2c",
      port: 161,
      timeout: 1000,
      retries: 3,
    },
    ai: {
      enabled: false,
      openaiApiKey: "",
      model: "gpt-4o",
    },
  };

  const [formData, setFormData] = useState<SettingsData>(settings || defaultSettings);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<SettingsData>) =>
      apiRequest("POST", "/api/settings", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/settings"] });
      toast({
        title: "Settings updated",
        description: "Your settings have been saved successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update settings: ${error}`,
        variant: "destructive",
      });
    },
  });

  // Early return - AFTER all hooks
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-64 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  const handleInputChange = (
    section: keyof SettingsData,
    field: string,
    value: string | number | boolean
  ) => {
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value,
      },
    });
  };

  const handleSaveSection = (section: keyof SettingsData) => {
    updateMutation.mutate({
      [section]: formData[section],
    });
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-dark">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure your monitoring system settings
        </p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="thresholds">Thresholds</TabsTrigger>
          <TabsTrigger value="snmp">SNMP</TabsTrigger>
          <TabsTrigger value="ai">AI Settings</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>
                Configure basic application settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="siteName">Site Name</Label>
                  <Input
                    id="siteName"
                    value={formData.general.siteName}
                    onChange={(e) =>
                      handleInputChange("general", "siteName", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="refreshInterval">Refresh Interval (seconds)</Label>
                  <Input
                    id="refreshInterval"
                    type="number"
                    min="10"
                    max="3600"
                    value={formData.general.refreshInterval}
                    onChange={(e) =>
                      handleInputChange(
                        "general",
                        "refreshInterval",
                        parseInt(e.target.value)
                      )
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => handleSaveSection("general")}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Settings</CardTitle>
              <CardDescription>
                Configure how you want to receive alerts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Notifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="emailEnabled" className="font-medium">Email Notifications</Label>
                  <Switch
                    id="emailEnabled"
                    checked={formData.notifications.emailEnabled}
                    onCheckedChange={(checked) =>
                      handleInputChange("notifications", "emailEnabled", checked)
                    }
                  />
                </div>
                {formData.notifications.emailEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="emailRecipients">Email Recipients (comma separated)</Label>
                    <Input
                      id="emailRecipients"
                      value={formData.notifications.emailRecipients}
                      onChange={(e) =>
                        handleInputChange(
                          "notifications",
                          "emailRecipients",
                          e.target.value
                        )
                      }
                    />
                  </div>
                )}
              </div>

              {/* SMS Notifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="smsEnabled" className="font-medium">SMS Notifications</Label>
                  <Switch
                    id="smsEnabled"
                    checked={formData.notifications.smsEnabled}
                    onCheckedChange={(checked) =>
                      handleInputChange("notifications", "smsEnabled", checked)
                    }
                  />
                </div>
                {formData.notifications.smsEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="smsRecipients">SMS Recipients (comma separated)</Label>
                    <Input
                      id="smsRecipients"
                      value={formData.notifications.smsRecipients}
                      onChange={(e) =>
                        handleInputChange(
                          "notifications",
                          "smsRecipients",
                          e.target.value
                        )
                      }
                    />
                  </div>
                )}
              </div>

              {/* Slack Notifications */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="slackEnabled" className="font-medium">Slack Notifications</Label>
                  <Switch
                    id="slackEnabled"
                    checked={formData.notifications.slackEnabled}
                    onCheckedChange={(checked) =>
                      handleInputChange("notifications", "slackEnabled", checked)
                    }
                  />
                </div>
                {formData.notifications.slackEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="slackWebhook">Slack Webhook URL</Label>
                    <Input
                      id="slackWebhook"
                      value={formData.notifications.slackWebhook}
                      onChange={(e) =>
                        handleInputChange(
                          "notifications",
                          "slackWebhook",
                          e.target.value
                        )
                      }
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => handleSaveSection("notifications")}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Threshold Settings */}
        <TabsContent value="thresholds">
          <Card>
            <CardHeader>
              <CardTitle>Threshold Settings</CardTitle>
              <CardDescription>
                Configure when alerts should be triggered
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* CPU Thresholds */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">CPU Utilization Thresholds (%)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cpuWarning">Warning Threshold</Label>
                    <Input
                      id="cpuWarning"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.thresholds.cpuWarning}
                      onChange={(e) =>
                        handleInputChange(
                          "thresholds",
                          "cpuWarning",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cpuCritical">Critical Threshold</Label>
                    <Input
                      id="cpuCritical"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.thresholds.cpuCritical}
                      onChange={(e) =>
                        handleInputChange(
                          "thresholds",
                          "cpuCritical",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Memory Thresholds */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Memory Usage Thresholds (%)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="memoryWarning">Warning Threshold</Label>
                    <Input
                      id="memoryWarning"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.thresholds.memoryWarning}
                      onChange={(e) =>
                        handleInputChange(
                          "thresholds",
                          "memoryWarning",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="memoryCritical">Critical Threshold</Label>
                    <Input
                      id="memoryCritical"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.thresholds.memoryCritical}
                      onChange={(e) =>
                        handleInputChange(
                          "thresholds",
                          "memoryCritical",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Disk Thresholds */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Disk Space Thresholds (%)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="diskWarning">Warning Threshold</Label>
                    <Input
                      id="diskWarning"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.thresholds.diskWarning}
                      onChange={(e) =>
                        handleInputChange(
                          "thresholds",
                          "diskWarning",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="diskCritical">Critical Threshold</Label>
                    <Input
                      id="diskCritical"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.thresholds.diskCritical}
                      onChange={(e) =>
                        handleInputChange(
                          "thresholds",
                          "diskCritical",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => handleSaveSection("thresholds")}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SNMP Settings */}
        <TabsContent value="snmp">
          <Card>
            <CardHeader>
              <CardTitle>SNMP Settings</CardTitle>
              <CardDescription>
                Configure global SNMP settings for device monitoring
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="communityString">Community String</Label>
                  <Input
                    id="communityString"
                    value={formData.snmp.communityString}
                    onChange={(e) =>
                      handleInputChange("snmp", "communityString", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="version">SNMP Version</Label>
                  <select
                    id="version"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={formData.snmp.version}
                    onChange={(e) =>
                      handleInputChange("snmp", "version", e.target.value)
                    }
                  >
                    <option value="1">Version 1</option>
                    <option value="2c">Version 2c</option>
                    <option value="3">Version 3</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="port">Port</Label>
                  <Input
                    id="port"
                    type="number"
                    value={formData.snmp.port}
                    onChange={(e) =>
                      handleInputChange(
                        "snmp",
                        "port",
                        parseInt(e.target.value)
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timeout">Timeout (ms)</Label>
                  <Input
                    id="timeout"
                    type="number"
                    value={formData.snmp.timeout}
                    onChange={(e) =>
                      handleInputChange(
                        "snmp",
                        "timeout",
                        parseInt(e.target.value)
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retries">Retries</Label>
                  <Input
                    id="retries"
                    type="number"
                    min="0"
                    max="10"
                    value={formData.snmp.retries}
                    onChange={(e) =>
                      handleInputChange(
                        "snmp",
                        "retries",
                        parseInt(e.target.value)
                      )
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => handleSaveSection("snmp")}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Settings */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle>AI Settings</CardTitle>
              <CardDescription>
                Configure AI features for anomaly detection and predictive analytics
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* AI Enable/Disable */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="aiEnabled" className="font-medium">Enable AI Features</Label>
                  <Switch
                    id="aiEnabled"
                    checked={formData.ai?.enabled || false}
                    onCheckedChange={(checked) =>
                      handleInputChange("ai", "enabled", checked)
                    }
                  />
                </div>
              </div>

              {formData.ai?.enabled && (
                <>
                  {/* OpenAI API Key */}
                  <div className="space-y-2">
                    <Label htmlFor="openaiApiKey">OpenAI API Key</Label>
                    <div className="relative">
                      <Input
                        id="openaiApiKey"
                        type="password"
                        value={formData.ai?.openaiApiKey || ""}
                        onChange={(e) =>
                          handleInputChange("ai", "openaiApiKey", e.target.value)
                        }
                        placeholder="Enter your OpenAI API key"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      You can obtain an API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">OpenAI</a>.
                    </p>
                  </div>

                  {/* AI Model Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="aiModel">AI Model</Label>
                    <select
                      id="aiModel"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={formData.ai?.model || "gpt-4o"}
                      onChange={(e) =>
                        handleInputChange("ai", "model", e.target.value)
                      }
                    >
                      <option value="gpt-4o">GPT-4o (Recommended)</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      GPT-4o is the latest model and offers the best performance.
                    </p>
                  </div>
                </>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={() => handleSaveSection("ai")}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
